/*
 * Narutieum Monitor
 * Copyright (c) 2026 Narutieum
 * Licensed under GPLv3.
 * License guide: https://narutieum.com/bbs/content.php?co_id=naru_license
 */

import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  acknowledgeMonitorAlert,
  fetchMonitorAlerts,
  getStoredMonitorSession,
} from "../api/client";
import AlertHistoryModal from "../pages/alerts/AlertHistoryModal";
import AlertSidebar from "../pages/alerts/AlertSidebar";
import {
  applyAcknowledgedStatus,
  loadAcknowledgedAlerts,
  saveAcknowledgedAlerts,
} from "../pages/alerts/alert-utils";
import "./AppShell.css";

const NAV_ITEMS = [
  { to: "/main", label: "메인", match: (pathname) => pathname === "/main" || pathname === "/" },
  {
    to: "/monitor",
    label: "그룹",
    match: (pathname) => pathname === "/monitor" || pathname.startsWith("/monitor/groups/"),
  },
  {
    to: "/monitor/devices",
    label: "디바이스",
    match: (pathname) => pathname === "/monitor/devices" || pathname.startsWith("/monitor/devices/"),
  },
];

export default function AppShell({ title, subtitle, actions, contentStyle, theme = "clear", themeStyle, brandName = "나루틔움", brandLogoUrl = "", children }) {
  const location = useLocation();
  const session = getStoredMonitorSession();
  const navItems = session ? NAV_ITEMS : [];
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [alerts, setAlerts] = React.useState([]);
  const [isAlertPanelOpen, setIsAlertPanelOpen] = React.useState(false);
  const [isAlertHistoryOpen, setIsAlertHistoryOpen] = React.useState(false);
  const [acknowledgedAlerts, setAcknowledgedAlerts] = React.useState(() => loadAcknowledgedAlerts());
  const [busyAlertIds, setBusyAlertIds] = React.useState([]);

  const closeSidebar = React.useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  React.useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  React.useEffect(() => {
    if (!session) {
      setAlerts([]);
      return;
    }

    let mounted = true;
    const loadAlerts = () => {
      Promise.all([
        fetchMonitorAlerts("active", 20),
        fetchMonitorAlerts("history", 80),
      ])
        .then(([activeData, historyData]) => {
          if (!mounted) return;
          const merged = new Map();
          [...(activeData.alerts || []), ...(historyData.alerts || [])].forEach((item) => {
            if (item?.id) {
              merged.set(item.id, item);
            }
          });
          setAlerts(applyAcknowledgedStatus(Array.from(merged.values()), acknowledgedAlerts));
        })
        .catch(() => {
          if (!mounted) return;
          setAlerts([]);
        });
    };

    loadAlerts();
    const timer = window.setInterval(loadAlerts, 5000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [acknowledgedAlerts, session]);

  const handleAlertConfirm = React.useCallback((alert) => {
    if (!alert?.id) {
      return;
    }
    setBusyAlertIds((prev) => [...prev, alert.id]);
    acknowledgeMonitorAlert(alert.id)
      .then((data) => {
        const serverAlert = data?.alert || null;
        setAlerts((prev) =>
          prev.map((item) => (
            item.id === alert.id
              ? { ...item, ...(serverAlert || {}), status: "acknowledged" }
              : item
          ))
        );
        setAcknowledgedAlerts((prev) => {
          const next = {
            ...(prev || {}),
            [alert.id]: new Date().toISOString(),
          };
          saveAcknowledgedAlerts(next);
          return next;
        });
      })
      .finally(() => {
        setBusyAlertIds((prev) => prev.filter((id) => id !== alert.id));
      });
  }, []);

  return (
    <div
      className={`${session ? "shell-wrap" : "shell-wrap guestmode"} shell-theme-${theme || "clear"}`}
      style={themeStyle || undefined}
    >
      <div className="shell-stage">
        <div className="shell-frame">
          <header className={session ? "shell-frame__top" : "shell-frame__top guestmode"}>
            <div className="shell-frame__brand">
              
              <Link to={session ? "/main" : "/login"} className="shell-frame__brand-link">
                {brandLogoUrl ? (
                  <img src={brandLogoUrl} alt={brandName || "로고"} className="shell-frame__brand-logo" />
                ) : (
                  <strong>{brandName || "나루틔움"}</strong>
                )}
              </Link>
              {/* <span className="shell-frame__route">{session ? "모니터 포털" : "로그인 화면"}</span> */}
            </div>
            <div className="shell-frame__top-actions">
              {session ? (
                <div className="shell-frame__session">
                  <span>{session.name || session.username || "monitor"} 님</span>
                </div>
              ) : null}
              {actions ? <div className="shell__actions">{actions}</div> : null}
              <span className="empty-top-btn"></span>
              {session ? (
                <button
                  type="button"
                  className="shell-frame__menu-button"
                  aria-label="모니터링 사이드 메뉴 열기"
                  aria-expanded={isSidebarOpen}
                  onClick={() => setIsSidebarOpen((prev) => !prev)}
                >
                  <span />
                  <span />
                  <span />
                </button>
              ) : null}
            </div>
          </header>

          {session ? (
            <>
              <div
                className={`shell-sidebar-backdrop ${isSidebarOpen ? "is-open" : ""}`}
                onClick={closeSidebar}
                aria-hidden={isSidebarOpen ? "false" : "true"}
              />
              <aside
                className={`shell-sidebar ${isSidebarOpen ? "is-open" : ""}`}
                aria-label="모니터링 사이드 메뉴"
              >
                <div className="shell-sidebar__header">
                  <div>
                    <span className="shell-sidebar__eyebrow">MONITORING</span>
                    <strong>사이드 메뉴</strong>
                  </div>
                  <button
                    type="button"
                    className="shell-sidebar__close"
                    onClick={closeSidebar}
                    aria-label="사이드 메뉴 닫기"
                  >
                    닫기
                  </button>
                </div>

                <div className="shell-sidebar__section">
                  <span className="shell-sidebar__section-title">바로 이동</span>
                  <div className="shell-sidebar__links">
                    {navItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={item.match(location.pathname) ? "active" : ""}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="shell-sidebar__section">
                  <span className="shell-sidebar__section-title">모니터링 준비 영역</span>
                  <div className="shell-sidebar__placeholders">
                    <div className="shell-sidebar__placeholder">
                      <strong>공통 확장 영역</strong>
                      <span>모니터링 전용 도구, 고정 위젯, 운영 보조 기능을 나중에 붙일 수 있는 자리</span>
                    </div>
                    <div className="shell-sidebar__placeholder">
                      <strong>대시보드 단축</strong>
                      <span>자주 보는 디바이스, 고정 링크, 운영 도구를 붙일 수 있는 공통 영역</span>
                    </div>
                  </div>
                </div>
              </aside>
            </>
          ) : null}

          <div className="shell">
            <header className={session ? "shell__header" : "shell__header guestmode"}>
              <div>
                <h1>{title}</h1>
                {subtitle ? <p>{subtitle}</p> : null}
              </div>
            </header>
            <main className="shell__content" style={contentStyle || undefined}>{children}</main>
          </div>

          <footer className={session ? "shell-footer" : "shell-footer guestmode"}>
            <div className="shell-footer__left">
              <span>© 2026 {brandName || "나루틔움"}. All rights reserved.</span>
            </div>
          </footer>
        </div>

        {session ? (
          <div className={`shell-stage__alerts ${isAlertPanelOpen ? "is-open" : "is-collapsed"}`}>
            <AlertSidebar
              open={isAlertPanelOpen}
              alerts={alerts}
              onToggle={() => setIsAlertPanelOpen((prev) => !prev)}
              onOpenHistory={() => setIsAlertHistoryOpen(true)}
              onConfirm={handleAlertConfirm}
              busyIds={busyAlertIds}
            />
          </div>
        ) : null}
      </div>

      {session ? (
        <AlertHistoryModal
          open={isAlertHistoryOpen}
          alerts={alerts}
          onClose={() => setIsAlertHistoryOpen(false)}
        />
      ) : null}
    </div>
  );
}
